import React, { useState } from 'react';
import Seo from '../components/Seo';
import { Link } from 'react-router-dom';
import { MapPin, Ship, Globe2, ArrowRight, Building2 } from 'lucide-react';
import PageHero from '../components/PageHero';
import Reveal from '../components/Reveal';
import WorldMap from '../components/WorldMap';
import { globalRegions, markets, contact } from '../mock/mock';

const GlobalReach = () => {
  const [active, setActive] = useState(globalRegions[0].id);
  const activeRegion = globalRegions.find((r) => r.id === active);

  return (
    <div>
      <Seo title="Global Reach — 30+ Export Markets" description="Kassam Trading Company exports to 30+ countries across the Gulf, East & West Africa, Southeast Asia, Europe and the Americas from Port Qasim, Karachi." path="/global-reach" />
      <PageHero
        title="Global Reach"
        subtitle="From our own mill at Port Qasim, we supply importers, distributors and re-exporters across 30+ countries. Explore the regions we serve."
        crumb="Global Reach"
      />

      <section className="max-w-7xl mx-auto px-6 py-16">
        <div className="grid lg:grid-cols-3 gap-10 items-start">
          {/* Map */}
          <Reveal className="lg:col-span-2 bg-ktc-ivoryLight rounded-2xl border border-ktc-cream p-4 md:p-6">
            <WorldMap regions={globalRegions} activeId={active} onSelect={setActive} />
            <div className="flex items-center gap-6 px-2 mt-3">
              <span className="flex items-center gap-2 text-xs text-ktc-charcoal/70"><span className="h-3 w-3 rounded-full bg-ktc-green" /> Regional hubs</span>
              <span className="flex items-center gap-2 text-xs text-ktc-charcoal/70"><span className="h-3 w-3 rounded-full bg-ktc-gold" /> Export destinations</span>
            </div>
            <p className="text-xs text-ktc-charcoal/50 mt-2 px-2">Markers show regional hubs and specific export destinations. Tap a marker or a region to view details.</p>
          </Reveal>

          {/* Region details */}
          <Reveal delay={100} className="space-y-3 max-h-[560px] overflow-y-auto pr-1">
            {globalRegions.map((r) => {
              const isActive = active === r.id;
              return (
                <button
                  key={r.id}
                  onClick={() => setActive(r.id)}
                  className={`w-full text-left rounded-xl border p-4 transition-colors ${isActive ? 'bg-ktc-green border-ktc-green text-ktc-ivory' : 'bg-ktc-ivoryLight border-ktc-cream hover:border-ktc-gold'}`}
                >
                  <div className="flex items-center gap-2">
                    <MapPin size={16} className={r.type === 'market' ? 'text-ktc-gold' : (isActive ? 'text-ktc-goldLight' : 'text-ktc-green')} />
                    <span className={`font-serif text-lg font-semibold ${isActive ? 'text-ktc-ivory' : 'text-ktc-green'}`}>{r.region}</span>
                    {r.type === 'market' && <span className={`ml-auto text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full ${isActive ? 'bg-ktc-gold/30 text-ktc-goldLight' : 'bg-ktc-gold/15 text-ktc-gold'}`}>Export</span>}
                  </div>
                  <div className={`text-xs mt-1 ${isActive ? 'text-ktc-ivory/70' : 'text-ktc-charcoal/55'}`}>{r.markets}{r.product ? ` \u00b7 ${r.product}` : ''}</div>
                  {isActive && <p className="text-sm text-ktc-ivory/85 mt-2 leading-relaxed">{r.blurb}</p>}
                </button>
              );
            })}
          </Reveal>
        </div>
      </section>

      {/* Highlights */}
      <section className="bg-ktc-cream/60">
        <div className="max-w-7xl mx-auto px-6 py-16 grid sm:grid-cols-3 gap-6">
          {[
            { icon: Globe2, t: '30+ Countries Served', d: 'Across the Gulf, Africa, Asia, Europe and the Americas.' },
            { icon: Ship, t: 'FOB Port Qasim', d: 'CIF pricing available to all major ports worldwide.' },
            { icon: Building2, t: 'North America Office', d: `${contact.naOffice}` },
          ].map((x, i) => (
            <Reveal key={x.t} delay={i * 80} className="bg-ktc-ivoryLight rounded-xl border border-ktc-cream p-7 card-lift">
              <div className="h-12 w-12 grid place-items-center rounded-lg bg-ktc-green text-ktc-goldLight mb-4"><x.icon size={22} /></div>
              <h3 className="font-serif text-2xl font-semibold text-ktc-green">{x.t}</h3>
              <p className="text-sm text-ktc-charcoal/70 mt-2">{x.d}</p>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-6 py-16 text-center">
        <Reveal>
          <div className="flex flex-wrap justify-center gap-2 mb-8">
            {markets.map((m) => <span key={m} className="px-4 py-2 rounded-full border border-ktc-green/20 text-sm text-ktc-charcoal/75">{m}</span>)}
          </div>
          <h2 className="font-serif text-4xl font-semibold text-ktc-green">Don't see your market?</h2>
          <p className="text-ktc-charcoal/70 mt-4">We ship worldwide. Tell us your destination port and we'll quote you.</p>
          <Link to="/quote" className="inline-flex items-center gap-2 bg-ktc-green hover:bg-ktc-greenLight text-ktc-ivory font-semibold px-8 py-3.5 rounded-md mt-8 transition-colors">Request a Quote <ArrowRight size={18} /></Link>
        </Reveal>
      </section>
    </div>
  );
};

export default GlobalReach;
